import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../hooks/useChat';
import { useHostBridge } from '../../context/HostBridgeContext';
import { ReferenceActionProvider } from '../../context/ReferenceActionContext';
import { ComposeMessageProvider } from '../../context/ComposeMessageContext';
import type { ChatReference, HostSession } from '../../interfaces/host';
import { ChatHeader } from '../ChatHeader';
import { MessageList } from '../MessageList';
import { ChatInput } from '../ChatInput';
import { ErrorBanner } from '../ErrorBanner';
import { GateState } from '../GateState';
import styles from './ChatPage.module.css';

interface ChatPageProps {
  session: HostSession;
}

/**
 * The chat surface. Owns the conversation via useChat, derives the header status
 * from the connection state, and lays out header / thread / composer to fill the
 * iframe. Auth + the target chatbot come from the host handshake (session prop).
 */
export function ChatPage({ session }: ChatPageProps) {
  const { t } = useTranslation();
  const { postToHost, isStandalone, signOut, endSessionSignal } = useHostBridge();
  const {
    sessionId,
    messages,
    connectionState,
    isStreaming,
    statusMessage,
    error,
    setError,
    startSession,
    endSession,
    sendMessage,
  } = useChat(session.chatbotId, session.chatbotVersionId);

  const [draft, setDraft] = useState('');
  // Gates the panel behind a loading state until the very first startSession
  // resolves — otherwise a resumed session flashes an empty thread for a beat
  // before the restored history pops in, which reads as janky/insecure rather
  // than seamless. Only the initial mount is gated; "New Chat" resets happen
  // while the panel is already visible and don't need this.
  const [isRestoring, setIsRestoring] = useState(true);
  const startedRef = useRef(false);
  const endSignalRef = useRef(endSessionSignal);

  // Start a session once per chatbot — resuming `session.sessionId` if the
  // host provided one, otherwise creating a new one. The SSE stream connects
  // automatically. Note: we deliberately do NOT tear down the session on
  // unmount — the iframe may simply be hidden/removed because the host is
  // switching to another parent app, and the session (plus its localStorage
  // history) must survive that so it can be resumed later. Destruction only
  // happens via an explicit `archer-chat:end-session` from the host.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void startSession(session.sessionId)
      .then((id) => {
        if (id) postToHost({ type: 'archer-chat:event', event: 'session-started', sessionId: id });
      })
      .finally(() => setIsRestoring(false));
  }, [startSession, postToHost, session.sessionId]);

  // The host explicitly told us the user ended the chat (as opposed to just
  // navigating away from this app) — destroy the session for real.
  useEffect(() => {
    if (endSessionSignal === endSignalRef.current) return;
    endSignalRef.current = endSessionSignal;
    void endSession().then(() => postToHost({ type: 'archer-chat:event', event: 'session-ended' }));
  }, [endSessionSignal, endSession, postToHost]);

  const handleNewChat = useCallback(async () => {
    if (messages.length > 0 && !window.confirm(t('ChatbotUI.NewChatConfirm'))) return;
    await endSession();
    postToHost({ type: 'archer-chat:event', event: 'session-ended' });
    const id = await startSession();
    if (id) postToHost({ type: 'archer-chat:event', event: 'session-started', sessionId: id });
  }, [messages.length, endSession, startSession, postToHost, t]);

  // A reference (deep link) in an assistant message was clicked — forward it to
  // the host, which owns navigation. In dev/standalone there's no host, so log
  // it instead so the behaviour is still observable while testing.
  const handleReference = useCallback(
    (reference: ChatReference) => {
      if (isStandalone) {
        console.info('[archer-chat] navigate reference (no host in standalone):', reference);
        return;
      }
      postToHost({ type: 'archer-chat:navigate', reference });
    },
    [postToHost, isStandalone],
  );

  const statusText = useMemo(() => {
    if (isStreaming) return t('ChatbotUI.StatusThinking');
    if (connectionState.status === 'connecting') return t('ChatbotUI.StatusConnecting');
    if (connectionState.status === 'error') return t('ChatbotUI.StatusReconnecting');
    return t('ChatbotUI.StatusOnline');
  }, [isStreaming, connectionState.status, t]);

  const online = connectionState.status === 'connected' || isStreaming;
  const composerDisabled = isStreaming || !sessionId;

  if (isRestoring) {
    return (
      <GateState
        icon="creation"
        spinning
        title={t('ChatbotUI.RestoringSession')}
        body={t('ChatbotUI.RestoringSessionHint')}
      />
    );
  }

  return (
    <div className={styles.panel} data-testid="chat-container">
      <ChatHeader
        statusText={statusText}
        online={online}
        onNewChat={handleNewChat}
        onLogout={import.meta.env.DEV && isStandalone ? signOut : undefined}
      />

      <ReferenceActionProvider onReference={handleReference}>
        <ComposeMessageProvider onCompose={setDraft}>
          <MessageList
            messages={messages}
            isStreaming={isStreaming}
            statusMessage={statusMessage}
            onRespondHalt={sendMessage}
          />
        </ComposeMessageProvider>
      </ReferenceActionProvider>

      <footer className={styles.footer}>
        {error ? (
          // Always retry by starting a fresh session — a session that just
          // errored (e.g. an expired token with no way to refresh) isn't
          // worth resuming as-is, and `startSession` resets to a clean slate.
          <ErrorBanner message={error} onRetry={startSession} onDismiss={() => setError(null)} />
        ) : null}
        <ChatInput value={draft} onChange={setDraft} disabled={composerDisabled} onSend={sendMessage} />
      </footer>
    </div>
  );
}
